import type { RequestDocument } from 'graphql-request';

const CLIP_COMMENT_FIELDS = `
  id
  commentContentType
  videoId
  videoTimeSeconds
  body
  authorId
  authorName
  authorEmail
  isGuest
  commentParentId
  resolvedAt
  reactions
  mentions
  drawing
  commentCreatedAt
  commentEditedAt
`;

/**
 * `drawing` (PLAT-11285) is selected above only because its field now exists on
 * the model. DatoCMS rejects a query naming an absent field — the whole request
 * fails and takes the entire Comments tab down, not just the marks — so if this
 * query is ever pointed at an environment where the migration has not run, that
 * line has to come out again.
 *
 * Applied via the DatoCMS schema editor rather than `migrations:run`: that
 * command replays from an empty `schema_migration` tracker in this project and
 * dies trying to re-create `clip_comment`, so the checklist and the real schema
 * have drifted. Verified present in `dev` (the environment the app reads via the
 * `X-Environment` header) — NOT yet in primary.
 */

/**
 * All clip comments for one Video record (top-level + replies), oldest first.
 * Scoped to `commentContentType: clip` so non-clip comments (pull quotes,
 * articles, …) can share the model without leaking into the clip surface.
 * Paginated by the repository.
 */
export const CLIP_COMMENTS_QUERY: RequestDocument = `
  query getClipComments($videoId: String!, $first: IntType!, $skip: IntType!) {
    allClipComments(
      filter: {
        videoId: { eq: $videoId }
        commentContentType: { eq: "clip" }
        isDeleted: { eq: false }
      }
      orderBy: commentCreatedAt_ASC
      first: $first
      skip: $skip
    ) {
      ${CLIP_COMMENT_FIELDS}
    }
  }
`;

/**
 * Direct, not-yet-deleted replies to a comment — used to cascade the soft
 * delete of a thread. Already-deleted replies are excluded (nothing to do).
 */
export const CLIP_COMMENT_REPLIES_QUERY: RequestDocument = `
  query getClipCommentReplies($parentId: String!) {
    allClipComments(
      filter: { commentParentId: { eq: $parentId }, isDeleted: { eq: false } }
      first: 100
    ) {
      id
    }
  }
`;

/**
 * Clip comments for a batch of Video records (newest first), filtered by
 * `videoId in`. Scoped to `commentContentType: clip` so the per-clip counts /
 * latest-comment previews don't include non-clip comments sharing the model.
 * The repository paginates this and groups client-side to derive each video's
 * count, open/resolved thread breakdown, and most-recent comment — one request
 * instead of a per-clip alias batch.
 */
export const CLIP_COMMENT_COUNTS_QUERY: RequestDocument = `
  query getClipCommentCounts($ids: [String], $first: IntType!, $skip: IntType!) {
    allClipComments(
      filter: {
        videoId: { in: $ids }
        commentContentType: { eq: "clip" }
        isDeleted: { eq: false }
      }
      orderBy: commentCreatedAt_DESC
      first: $first
      skip: $skip
    ) {
      videoId
      body
      authorName
      commentCreatedAt
      commentParentId
      resolvedAt
    }
  }
`;
